import type { UserAttributeKey } from '@aws-amplify/auth';

type StoredUserData = Partial<Record<UserAttributeKey, string>> & {
  username?: string;
};

interface LegacyUserAttribute {
  Name: string;
  Value: string;
}

interface LegacyUserData {
  UserAttributes: LegacyUserAttribute[];
  Username?: string | null;
}

const USER_DATA_KEY = 'userData';
const LEGACY_USER_DATA_KEY = '.userData';

/**
 * 旧実装が ID トークンを保存していたキー。
 * 現在は localStorage に ID トークンを保持しない（取得は fetchAuthSession() のみ）。
 * 移行前のブラウザに残った値を消すためだけに参照する。
 */
const OBSOLETE_ID_TOKEN_KEYS = ['app.idToken', '.idToken'] as const;

function isLegacyUserData(value: unknown): value is LegacyUserData {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return Array.isArray((value as LegacyUserData).UserAttributes);
}

function normalizeLegacyUserData(userData: LegacyUserData): StoredUserData {
  const normalized: StoredUserData = {};

  userData.UserAttributes.forEach((attribute) => {
    normalized[attribute.Name as UserAttributeKey] = attribute.Value;
  });

  if (userData.Username) {
    normalized.username = userData.Username;
  }

  return normalized;
}

function parseStoredUserData(item: string): StoredUserData | null {
  try {
    const parsed = JSON.parse(item) as unknown;

    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    if (isLegacyUserData(parsed)) {
      return normalizeLegacyUserData(parsed);
    }

    return parsed as StoredUserData;
  } catch (error) {
    console.error('[localStorage] invalid userData JSON', error);
    return null;
  }
}

function getStoredUserData(): StoredUserData | null {
  const currentItem = localStorage.getItem(USER_DATA_KEY);
  if (currentItem !== null) {
    return parseStoredUserData(currentItem);
  }

  const legacyItem = localStorage.getItem(LEGACY_USER_DATA_KEY);
  if (legacyItem === null) {
    return null;
  }

  const normalized = parseStoredUserData(legacyItem);
  if (normalized !== null) {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(normalized));
  }

  return normalized;
}

export function localStorageSetUserItem(attributes: StoredUserData): void {
  console.log('User Attributes:', attributes);
  console.log('custom:chain', attributes['custom:chain']);
  console.log('custom:group_name', attributes['custom:group_name']);

  localStorage.setItem(USER_DATA_KEY, JSON.stringify(attributes));
}

/**
 * 移行前のブラウザに残っている ID トークンを削除する。
 * アプリ起動時に 1 回呼ぶ。サインアウトを挟まない限り古い値が残り続けるため。
 */
export function localStoragePurgeObsoleteIdToken(): void {
  OBSOLETE_ID_TOKEN_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });
}

export function localStorageClearAuthCache(): void {
  localStorage.removeItem(USER_DATA_KEY);
  localStorage.removeItem(LEGACY_USER_DATA_KEY);
  localStoragePurgeObsoleteIdToken();
}

/**
 * localStorage から完全一致キーの値を取得する。
 */
export function localStorageGetItem(key: string): string | null {
  return localStorage.getItem(key);
}

/**
 * localStorage から指定したキーの値を取得する。
 * 存在しなければ null を返す。
 */
export function localStorageGetUserData(key: string): string | null {
  const userData = getStoredUserData();
  if (userData === null) {
    return null;
  }

  return userData[key] ?? null;
}

/**
 * localStorage から menu_permission を取得する。
 * 存在しなければ null を返す。
 */
export function localStorageGetMenuPermission(): string[] | null {
  const menuPermission = localStorageGetUserData('custom:menu_permission');
  if (menuPermission === null) {
    return null;
  }

  try {
    return JSON.parse(menuPermission);
  } catch (error) {
    console.error('[localStorageGetMenuPermission] invalid JSON', error);
    return null;
  }
}

export function localStorageGetCustomerCode(): string {
  return localStorageGetUserData('custom:customer') ?? '';
}

export function localStorageGetChainCode(): string {
  return localStorageGetUserData('custom:chain') ?? '';
}

export function localStorageGetShopCode(): string {
  return localStorageGetUserData('custom:shop') ?? '';
}

export function localStorageGetUserId(): string {
  return localStorageGetUserData('username') ?? '';
}

export function localStorageGetUserName(): string {
  return localStorageGetUserData('name') ?? '';
}

export function localStorageGetUserEmail(): string {
  return localStorageGetUserData('email') ?? '';
}

export function localStorageGetPersonalInfoDl(): string {
  return localStorageGetUserData('custom:personal_info_dl') ?? '';
}

export function localStorageGetPersonalInfo(): string {
  return localStorageGetUserData('custom:personal_info') ?? '';
}

export function localStorageGetAuthType(): string {
  return localStorageGetUserData('custom:auth_type') ?? '';
}

export function localStorageGetGroup(): string {
  return localStorageGetUserData('custom:group') ?? '';
}

export function localStorageGetPageHeaders(pageName: string): string | null {
  const userId = localStorageGetUserId();
  if (!userId) {
    return null;
  }

  return localStorageGetItem(`${userId}_${pageName}`);
}

export function localStorageSetPageHeaders(
  pageName: string,
  value: string
): void {
  const userId = localStorageGetUserId();
  if (!userId) {
    return;
  }

  localStorage.setItem(`${userId}_${pageName}`, value);
}
